import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../lib/api';
import { daysOverdue, leadFollowupBucket } from '../../../shared/attention';
import { addDays, CalendarPanel } from '../../components/primitives';
import { ArrowIcon, CalendarIcon, CheckIcon, ClockIcon, MClockIcon, MoreIcon, SnoozeIcon } from '../../components/icons';
import { DateSheet, defaultDateChips } from '../../components/DateSheet';
import { PageMenu } from '../../components/PageMenu';
import { SwipeRow } from '../../components/SwipeRow';
import { useIsMobile } from '../../lib/useIsMobile';
import { reportError } from '../../lib/errorEvents';
import type { Followup } from './types';

/** Lead follow-up attention row — the date is editable right here (branded
    picker + lead PATCH) so a date can be pushed without opening the lead. */
export function FollowupAttentionRow({
  lead,
  today,
  onChanged,
}: {
  lead: Followup;
  today: string;
  onChanged: () => void;
}) {
  const navigate = useNavigate();
  const [pickOpen, setPickOpen] = useState(false);
  const metaRef = useRef<HTMLSpanElement>(null);
  const isMobile = useIsMobile();
  const [rowSheet, setRowSheet] = useState<null | 'more' | 'date' | 'next'>(null);
  const bucket = leadFollowupBucket(lead.followup_date, today);
  const days = lead.followup_date ? daysOverdue(lead.followup_date, today) : 0;

  const saveDate = async (iso: string | null): Promise<boolean> => {
    const ok = await api(`/api/admin/leads/${lead.id}`, {
      method: 'PATCH',
      json: { followup_date: iso },
    })
      .then(() => true)
      .catch((error) => {
        reportError(error, 'Could not update the follow-up date.');
        return false;
      });
    onChanged();
    return ok;
  };

  const bucketText =
    bucket === 'missing' ? 'No date — set one' : bucket === 'overdue' ? `${days}d overdue` : 'Today';
  const bucketTone = bucket === 'overdue' ? 'd' : bucket === 'missing' ? 'g' : undefined;

  // "Done" = the follow-up happened: the date is consumed, and since an open
  // lead with no date stays in this list by design (phase 23), the next
  // follow-up sheet opens at once so the row can leave in one gesture.
  // Cancelling it leaves the row reading "No date — set one".
  const markDone = async () => {
    if (await saveDate(null)) setRowSheet('next');
  };

  if (isMobile) {
    return (
      <>
        <SwipeRow
          rightAction={{
            label: 'Done',
            icon: <CheckIcon />,
            tone: 'ok',
            onSelect: () => void markDone(),
          }}
          leftActions={[
            { label: 'Date', icon: <CalendarIcon />, tone: 'gold', onSelect: () => setRowSheet('date') },
            { label: 'More', icon: <MoreIcon />, tone: 'grey', onSelect: () => setRowSheet('more') },
          ]}
        >
          <div className="row mrow-att" onClick={() => navigate(`/leads/${lead.id}`)}>
            <span className="ico g">
              <MClockIcon />
            </span>
            <div className="main">
              <div className="ttl">Follow up — {lead.company ?? lead.name}</div>
              {/* The lead name gives way before the date does — a truncated
                  "66d overdue" is the half that matters. */}
              <div className="sub">
                <span className="who">Lead · {lead.name}</span>
                <span className="sep">·</span>
                <span className={bucketTone}>{bucketText}</span>
              </div>
            </div>
            <button
              type="button"
              className="rmore"
              aria-label="More actions"
              onClick={(e) => {
                e.stopPropagation();
                setRowSheet('more');
              }}
            >
              <MoreIcon />
            </button>
          </div>
        </SwipeRow>

        <PageMenu
          open={rowSheet === 'more'}
          onClose={() => setRowSheet(null)}
          items={[
            { key: 'done', icon: <CheckIcon />, label: 'Done', onSelect: () => void markDone() },
            {
              key: '3d',
              icon: <SnoozeIcon />,
              label: '+3 days',
              onSelect: () => void saveDate(addDays(today, 3)),
            },
            {
              key: '1w',
              icon: <SnoozeIcon />,
              label: '+1 week',
              onSelect: () => void saveDate(addDays(today, 7)),
            },
            {
              key: 'pick',
              icon: <CalendarIcon />,
              label: 'Pick date',
              onSelect: () => setRowSheet('date'),
            },
            {
              key: 'open',
              icon: <ArrowIcon />,
              label: 'Open lead',
              onSelect: () => navigate(`/leads/${lead.id}`),
            },
          ]}
        />

        <DateSheet
          open={rowSheet === 'date'}
          title="Follow-up date"
          context={lead.company ?? lead.name}
          value={lead.followup_date}
          allowClear
          chips={defaultDateChips(today)}
          onPick={(iso) => {
            setRowSheet(null);
            void saveDate(iso);
          }}
          onClose={() => setRowSheet(null)}
        />

        <DateSheet
          open={rowSheet === 'next'}
          title="Next follow-up"
          context={`${lead.company ?? lead.name} · followed up`}
          value={null}
          allowClear
          chips={defaultDateChips(today)}
          verb="Follow up"
          onPick={(iso) => {
            setRowSheet(null);
            if (iso) void saveDate(iso);
          }}
          onClose={() => setRowSheet(null)}
        />
      </>
    );
  }

  return (
    <div className="row" onClick={() => navigate(`/leads/${lead.id}`)} style={{ cursor: 'pointer' }}>
      <span className="row-icon ic-accent">
        <ClockIcon />
      </span>
      <span className="row-title" title={`Follow up — ${lead.company ?? lead.name}`}>
        Follow up — {lead.company ?? lead.name}
      </span>
      <span className="row-context" title={`Lead · ${lead.name}`}>Lead · {lead.name}</span>
      <span className="row-spacer" />
      <span
        ref={metaRef}
        className={`row-meta editable${bucket === 'overdue' ? ' danger' : bucket === 'missing' ? ' gold' : ''}`}
        onClick={(e) => {
          e.stopPropagation();
          setPickOpen((o) => !o);
        }}
      >
        {bucketText}
      </span>
      {pickOpen && (
        <CalendarPanel
          value={lead.followup_date}
          anchorRef={metaRef}
          onPick={(iso) => iso && saveDate(iso)}
          onClose={() => setPickOpen(false)}
        />
      )}
    </div>
  );
}
